import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Sigillo esagonale del v40: porting verbatim di `famecertBadgeHTML`
 * (riferimento, riga 4949). Geometrie e colori sono i suoi — bordo arancione
 * `#DF6616`, fondo blu `#0A1B38`, esagono della spunta verde `#5BCBBC` — e non
 * vanno reinterpretati: il v39 usava uno scudo a otto lati su viewBox 24×24.
 */
const SEAL_OUTER =
  'M105.59,16.00 L158.42,46.50 C166.04,50.90 172.27,61.70 172.27,70.50 L172.27,133.50 ' +
  'C172.27,142.30 166.04,153.10 158.42,157.50 L105.59,188.00 C97.01,192.95 82.99,192.95 74.41,188.00 ' +
  'L21.58,157.50 C13.96,153.10 7.73,142.30 7.73,133.50 L7.73,70.50 C7.73,61.70 13.96,50.90 21.58,46.50 ' +
  'L74.41,16.00 C82.99,11.05 97.01,11.05 105.59,16.00 Z';

const SEAL_INNER =
  'M103.50,27.66 L149.28,54.10 C155.88,57.91 161.28,67.27 161.28,74.89 L161.28,129.47 ' +
  'C161.28,137.09 155.88,146.45 149.28,150.26 L103.50,176.70 C96.08,180.97 83.92,180.97 76.50,176.70 ' +
  'L30.72,150.26 C24.12,146.45 18.72,137.09 18.72,129.47 L18.72,74.89 C18.72,67.27 24.12,57.91 30.72,54.10 ' +
  'L76.50,27.66 C83.92,23.39 96.08,23.39 103.50,27.66 Z';

/**
 * Badge Famecert dell'header brand, condiviso da Insight, Decision e
 * Intelligence.
 *
 * Dalla Specifica v2.4 gli stati sono due, `certificato` e `none`: qualunque
 * altro valore — inclusi i quattro dell'enum precedente (`in_progress`,
 * `active`, `expired`, `revoked`) — si rende come `none`. La normalizzazione è
 * già fatta all'import, questa qui è la seconda rete: il componente riceve una
 * stringa e non si fida.
 *
 * La classe CSS del caso certificato resta `certified`, non `certificato`: è
 * quella del riferimento e quella definita in `fametrue.css`. Il valore del
 * JSON e il nome della classe sono due cose diverse, e la seconda non va
 * tradotta.
 */
export function FamecertBadge({ status }: { status: string }): ReactNode {
  const { t } = useTranslation();
  const certified = status === 'certificato';
  const variant = certified ? 'certified' : 'none';

  return (
    <span className={`famecert-badge ${variant}`}>
      <svg viewBox="0 0 180 204">
        <path d={SEAL_OUTER} fill="#DF6616" />
        <path d={SEAL_INNER} fill="#0A1B38" />
        {certified ? (
          <>
            <polygon
              points="66,84 114,84 128,104 114,124 66,124 52,104"
              fill="#5BCBBC"
              stroke="#5BCBBC"
              strokeWidth="6.5"
              strokeLinejoin="round"
            />
            <path
              d="M78,104 L86,113 L102,93"
              fill="none"
              stroke="#0A1B38"
              strokeWidth="6.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        ) : null}
      </svg>
      {t(`famecert.${variant}`)}
    </span>
  );
}
